import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Clock, Users, Shield, Leaf } from 'lucide-react';
import SEO from '../components/SEO';
import { FadeIn, PageHero } from '../components/PageComponents';
import { oplossingenData } from '../data/geoData';

const solutionVisuals = [
  {
    image:
      'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1400&q=85&auto=format&fit=crop',
    icon: Clock,
    stat: '300+',
    statLabel: 'Succesvolle plaatsingen',
  },
  {
    image:
      'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1400&q=85&auto=format&fit=crop',
    icon: Users,
    stat: '150+',
    statLabel: 'Vaste plaatsingen',
  },
  {
    image:
      'https://images.unsplash.com/photo-1556761175-4b46a572b786?w=1400&q=85&auto=format&fit=crop',
    icon: Shield,
    stat: '85%',
    statLabel: 'Conversie naar vast',
  },
  {
    image:
      'https://images.unsplash.com/photo-1473968512647-3e447244af8f?w=1400&q=85&auto=format&fit=crop',
    icon: Leaf,
    stat: '50+',
    statLabel: 'Duurzaamheidsprojecten',
  },
];

const processSteps = [
  {
    nr: '01',
    title: 'Kennismaking',
    desc: 'Een persoonlijk gesprek om uw behoefte, cultuur en ambities te begrijpen.',
    image:
      'https://images.unsplash.com/photo-1552664730-d307ca884978?w=900&q=85&auto=format&fit=crop',
  },
  {
    nr: '02',
    title: 'Match & Voorstel',
    desc: 'Uit ons netwerk presenteren wij een kandidaat-shortlist die bij u past.',
    image:
      'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=900&q=85&auto=format&fit=crop',
  },
  {
    nr: '03',
    title: 'Start & Begeleiding',
    desc: 'De professional start en wij blijven betrokken met voortdurende begeleiding.',
    image:
      'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=900&q=85&auto=format&fit=crop',
  },
];

const OnzeOplossingenPage = () => (
  <main className="pt-28 min-h-screen">
    <SEO
      title="Onze Oplossingen - Staffing Force"
      description="Staffing Force biedt interim management, werving & selectie, detavast en duurzaamheidsconsultancy. Ontdek de juiste oplossing voor uw organisatie."
      keywords="interim management, werving selectie, detavast, duurzaamheid, oplossingen"
    />
    <PageHero
      label="Voor opdrachtgevers"
      title="Onze Oplossingen"
      description="Van interim management tot duurzaamheidsconsultancy – Staffing Force Nederland biedt de juiste oplossing voor elke vraag."
      image="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1920&q=85&auto=format&fit=crop"
    />

    {/* Solutions with images */}
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-28">
        {oplossingenData.map((o, i) => {
          const visual = solutionVisuals[i] || solutionVisuals[0];
          const Icon = visual.icon;
          const isReversed = i % 2 !== 0;
          return (
            <FadeIn key={i} delay={i * 0.05}>
              <div
                className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
                  isReversed ? 'lg:grid-flow-dense' : ''
                }`}
              >
                {/* Text */}
                <div className={isReversed ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-[#00897B]/10 flex items-center justify-center flex-shrink-0">
                      <Icon size={22} className="text-[#00897B]" />
                    </div>
                    <span className="text-[#00897B] text-xs font-semibold tracking-widest uppercase">
                      Oplossing 0{i + 1}
                    </span>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5 leading-tight">
                    {o.title}
                  </h2>
                  <p className="text-gray-600 leading-relaxed mb-7">{o.desc}</p>
                  <ul className="space-y-3 mb-8">
                    {o.features.map((f, fi) => (
                      <li key={fi} className="flex items-center gap-3 text-sm text-gray-700">
                        <CheckCircle size={18} className="text-[#00897B] flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/opdracht-opgeven"
                    className="inline-flex items-center gap-2 bg-[#00897B] hover:bg-[#00796B] text-white px-6 py-3 rounded font-semibold text-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#00897B]/25"
                  >
                    Opdracht opgeven <ArrowRight size={15} />
                  </Link>
                </div>

                {/* Image with stat */}
                <div className={`relative ${isReversed ? 'lg:col-start-1' : ''}`}>
                  <div className="relative rounded-2xl overflow-hidden aspect-[4/3] shadow-xl">
                    <img
                      src={visual.image}
                      alt={o.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#0d1b2a]/40 via-transparent to-transparent" />
                  </div>
                  {/* Floating stat */}
                  <div
                    className={`absolute -bottom-6 bg-white rounded-xl shadow-2xl p-5 border border-gray-100 w-44 ${
                      isReversed ? '-right-6 lg:-right-10' : '-left-6 lg:-left-10'
                    }`}
                  >
                    <p className="text-3xl md:text-4xl font-bold text-[#00897B] leading-none">
                      {visual.stat}
                    </p>
                    <p className="text-gray-600 text-xs mt-2 leading-tight">{visual.statLabel}</p>
                  </div>
                </div>
              </div>
            </FadeIn>
          );
        })}
      </div>
    </section>

    {/* Process steps with images */}
    <section className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <FadeIn>
          <div className="text-center mb-16">
            <span className="text-[#00897B] text-sm font-semibold tracking-widest uppercase">
              Onze aanpak
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3">
              Zo werken wij
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Van eerste kennismaking tot succesvolle plaatsing – een transparant proces met persoonlijke aandacht.
            </p>
          </div>
        </FadeIn>
        <div className="grid md:grid-cols-3 gap-8">
          {processSteps.map((s, i) => (
            <FadeIn key={s.nr} delay={i * 0.1}>
              <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1 group h-full flex flex-col">
                <div className="h-52 overflow-hidden relative">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-1.5">
                    <span className="text-[#00897B] font-bold text-sm">{s.nr}</span>
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{s.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    {/* Wide image banner / values */}
    <section className="relative py-32 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1920&q=85&auto=format&fit=crop"
          alt="Team van Finance en HR professionals in overleg – Staffing Force"
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d1b2a]/95 via-[#0d1b2a]/85 to-[#00897B]/60" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <FadeIn>
          <div>
            <span className="text-[#00bfa5] text-sm font-semibold tracking-widest uppercase">
              Waarom Staffing Force
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-6 leading-tight">
              Duurzaam, persoonlijk en resultaatgericht
            </h2>
            <p className="text-white/80 leading-relaxed text-lg">
              Sinds 2007 zijn wij het A-merk in Finance & HR. Als eerste detacheringsbureau in
              Nederland B Corp gecertificeerd. Wij kijken verder dan alleen de plaatsing.
            </p>
          </div>
        </FadeIn>
        <FadeIn delay={0.2}>
          <div className="grid grid-cols-2 gap-4">
            {[
              { k: '18+', l: 'Jaar ervaring' },
              { k: '500+', l: 'Geplaatste professionals' },
              { k: '200+', l: 'Opdrachtgevers' },
              { k: '9.2', l: 'Klantbeoordeling' },
            ].map((s, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <p className="text-3xl md:text-4xl font-bold text-white">{s.k}</p>
                <p className="text-white/70 text-sm mt-1">{s.l}</p>
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
    </section>

    {/* CTA */}
    <section className="py-20 bg-[#0d1b2a]">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <FadeIn>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Welke oplossing past bij u?</h2>
          <p className="text-white/70 mb-8">Neem contact op voor een vrijblijvend adviesgesprek.</p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link
              to="/contact"
              className="bg-[#00897B] hover:bg-[#00796B] text-white px-8 py-3 rounded font-semibold text-sm transition-all hover:-translate-y-0.5 inline-flex items-center gap-2"
            >
              Contact opnemen <ArrowRight size={16} />
            </Link>
            <Link
              to="/opdracht-opgeven"
              className="border-2 border-white/30 hover:border-white text-white px-8 py-3 rounded font-semibold text-sm transition-all hover:-translate-y-0.5"
            >
              Opdracht opgeven
            </Link>
          </div>
        </FadeIn>
      </div>
    </section>
  </main>
);

export default OnzeOplossingenPage;
